import { hasLapChildData } from './lapChild';

/**
 * Bus categories that count as a free fare (as opposed to `general` /
 * `oversold` adult tickets). Anything in this set means the passenger is
 * traveling under a regulated discount that the operator must validate at
 * the physical counter before issuing the boarding ticket.
 */
export const FREE_FARE_CATEGORIES = [
  'older',
  'older_half',
  'free_pass',
  'intermunicipal_free_pass',
  'disadvantaged_youth',
  'disadvantaged_youth_half',
];

/**
 * Whether a single bus_category value is a free fare.
 * @param {string} busCategory - The category to check.
 * @returns {boolean} True if the category is a free fare.
 */
export const isFreeFareCategory = (busCategory) => FREE_FARE_CATEGORIES.includes(busCategory);

/**
 * Bus categories the backend treats as an ANTT gratuidade, and therefore the ones whose
 * passenger must produce a document. Mirrors `Passenger::GRATUITY_BUS_CATEGORIES` in
 * platform-purchase: the six 100% tiers plus the three 50% half fares. A half fare granted
 * by a benefit programme still has to be substantiated.
 *
 * This deliberately overlaps {@link FREE_FARE_CATEGORIES} rather than replacing it. That list
 * is six entries and drives four unrelated decisions — the search redirect URL format
 * (`searchUrlHelpers`), whether the price breakdown auto-opens (`PurchasePricing`), the counter
 * notice and whether the order locator is hidden (`DownloadTickets*`). Widening it to match the
 * backend would change all four for brands that never asked. Two lists, two questions.
 */
export const GRATUITY_DOCUMENT_CATEGORIES = [
  'older',
  'older_half',
  'free_pass',
  'intermunicipal_free_pass',
  'disadvantaged_youth',
  'disadvantaged_youth_half',
  'disability',
  'disability_half',
  'federal_companion_free_pass',
];

/**
 * Whether a passenger in this bus category must produce an ANTT gratuidade document.
 *
 * Paid discounts — `student`, `teacher`, `minor`, `pet_friendly` — are not gratuidades and
 * answer false, even though the legacy `!busCategory.includes('general')` gate asks them for a
 * document too.
 *
 * @param {string} busCategory - The category to check.
 * @returns {boolean} True when the category is a regulated ANTT benefit.
 */
export const isGratuityDocumentCategory = (busCategory) =>
  GRATUITY_DOCUMENT_CATEGORIES.includes(busCategory);

/**
 * Categories that are an ordinary adult rather than a benefit.
 *
 * `oversold` is an adult in an overbooked seat, and in the dynamic form it is also the DEFAULT
 * whenever `overbooking` is on. The legacy gate asks `!busCategory.includes('general')`, and
 * `'oversold'.includes('general')` is false, so it let them through: that passenger was asked for
 * a gratuidade document they do not have, and since rendering the field is what makes it
 * required, they could not pay.
 *
 * The empty string is here for the same reason: a passenger who has not chosen a category yet has
 * not claimed a benefit, and `!''.includes('general')` is true.
 *
 * @type {string[]}
 */
const ORDINARY_ADULT_CATEGORIES = ['general', 'oversold', ''];

/**
 * Whether this category is an ordinary adult, and therefore excluded by every
 * `SHOW_*_EXCLUDING_ADULTS` flag -- not only the document one. `PersonPassengersForm` gates the
 * birth date with the same question.
 *
 * It keeps the legacy `includes('general')` alongside the exact list rather than matching only
 * the three literals: there are composite categories ending in `general` that the legacy gate
 * `!busCategory.includes('general')` has always treated as an ordinary adult, and narrowing this
 * to equality started showing them the document and the birth date in `PersonPassengersForm`
 * while the other two call sites kept hiding them -- the same disagreement `oversold` caused, one
 * release later. A regulated benefit still wins over the substring, so the day the backend ships
 * a gratuidade whose name contains `general` it keeps owing its document.
 *
 * @param {string} busCategory - The category the passenger chose.
 * @returns {boolean} True when the passenger is an ordinary adult.
 */
export const isOrdinaryAdultCategory = (busCategory) => {
  const category = String(busCategory || '');

  if (ORDINARY_ADULT_CATEGORIES.includes(category)) return true;

  return category.includes('general') && !isGratuityDocumentCategory(category);
};

/**
 * Whether this category can be asked for a document. Defined as the exact negation of
 * {@link isOrdinaryAdultCategory} on purpose: the two questions are asked by different forms
 * -- `PersonPassengersForm` asks the first, `DynamicPassengersForm` and `PassengersStatements`
 * ask the second -- and every bug here so far has been the two drifting apart. Written this way
 * they cannot.
 *
 * @param {string} busCategory - The category the passenger chose.
 * @returns {boolean} True when this category can be asked for the document.
 */
export const isDocumentableCategory = (busCategory) => !isOrdinaryAdultCategory(busCategory);

/**
 * Legacy aliases that only ever appear as a ticket `category`, never as a `bus_category`.
 * `special` and `wheelchair_handicap` are how older providers spell the PCD benefit.
 */
const LEGACY_TICKET_ALIASES = ['special', 'wheelchair_handicap'];

/**
 * Categories whose passenger has to be validated at the physical counter, and therefore drive
 * every purchase-complete decision about that: the counter notice, whether the order locator is
 * hidden, and whether the price breakdown opens by itself.
 *
 * This is the backend taxonomy plus the legacy ticket-only aliases. It is deliberately NOT
 * {@link FREE_FARE_CATEGORIES}, which stays at its original six because it also decides the
 * search redirect URL format — widening that would change the URL for brands that never asked.
 *
 * Splitting them this way fixes a real inconsistency: `disability` used to count for hiding the
 * locator and NOT for showing the counter notice, so a `disability`-only purchase lost its order
 * reference and got no explanation of why. 96 purchases over 180 days. Same for
 * `federal_companion_free_pass` and `disability_half`, which neither helper covered.
 */
const COUNTER_VALIDATED_CATEGORIES = [...GRATUITY_DOCUMENT_CATEGORIES, ...LEGACY_TICKET_ALIASES];

/**
 * Whether a category counts as counter-validated for the purchase-complete UI.
 * @param {string|undefined|null} category - Ticket category or passenger bus category.
 * @returns {boolean}
 */
export const isCounterValidatedCategory = (category) =>
  Boolean(category) && COUNTER_VALIDATED_CATEGORIES.includes(category);

/**
 * Whether a ticket `category` value counts as free fare for “all gratuity” purchase-complete UI.
 * @param {string|undefined|null} category - Ticket category from fragments.
 * @returns {boolean}
 */
export const isFreeFareTicketCategory = (category) => isCounterValidatedCategory(category);

/**
 * True when every fragment ticket is a free-fare category (no paid adult seats in the booking).
 * Used to hide digital ticket download UI on Purchase complete; callers should treat no tickets as false.
 * @param {object} purchase - Purchase object (toJS shape from redux).
 * @returns {boolean}
 */
export const purchaseIsAllFreeFareTickets = (purchase) => {
  if (!purchase) return false;

  const fragments = [
    ...((purchase.departs && purchase.departs.fragments) || []),
    ...((purchase.returns && purchase.returns.fragments) || []),
  ];
  const tickets = fragments.flatMap((fragment) => (fragment && fragment.tickets) || []);

  if (tickets.length === 0) return false;

  return tickets.every((ticket) => isFreeFareTicketCategory(ticket && ticket.category));
};

/**
 * Whether the purchase carries at least one passenger who has to be validated at the counter.
 * Uses {@link isCounterValidatedCategory}, the same predicate as
 * {@link purchaseIsAllFreeFareTickets}, so the counter notice and the hidden order locator can
 * never disagree about the same purchase. Walks four sources so the answer stays correct across
 * the full lifecycle: `passengers[].meta.lapChild` (lap children share an adult's
 * seat and travel free), ticket `category` after confirma-venda, the
 * `passengerSelection` count map once it has been dispatched, then the
 * `passengers[].busCategory` array which is populated during checkout
 * before either of the ticket / selection sources land.
 * @param {object} purchase - The purchase object (toJS shape from redux).
 * @returns {boolean} True if at least one passenger travels free (regulated discount or lap child).
 */
export const purchaseHasFreeFarePassenger = (purchase) => {
  if (!purchase) return false;

  const passengers = purchase.passengers || [];
  const hasLapChild = passengers.some((passenger) => hasLapChildData(passenger?.meta?.lapChild));
  if (hasLapChild) return true;

  const fragments = [
    ...((purchase.departs && purchase.departs.fragments) || []),
    ...((purchase.returns && purchase.returns.fragments) || []),
  ];
  const tickets = fragments.flatMap((fragment) => (fragment && fragment.tickets) || []);

  if (tickets.length > 0) {
    return tickets.some((ticket) => isCounterValidatedCategory(ticket && ticket.category));
  }

  const selection = purchase.passengerSelection || {};
  const selectionKeys = Object.keys(selection);
  if (selectionKeys.length > 0) {
    return selectionKeys.some(
      (category) => Number(selection[category]) > 0 && isCounterValidatedCategory(category),
    );
  }

  return passengers.some(
    (passenger) => passenger && isCounterValidatedCategory(passenger.busCategory),
  );
};

/**
 * The types the field offers in its `accept` AND the only ones the validator lets through.
 *
 * They live here rather than inside the component because an input's `accept` is only a filter on
 * the picker: anyone can choose "All files" and send a `.exe`, an `.html` or a renamed zip. If the
 * render and the validation each kept their own copy, they would drift.
 *
 * @type {string[]}
 */
export const ACCEPTED_DOCUMENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/bmp',
  'image/heic',
  'application/pdf',
  'image/tiff',
  'image/webp',
  'image/vnd.adobe.photoshop',
];

/**
 * What a browser writes when it does not recognise the format: `File.type` comes back empty and
 * `readAsDataURL` falls back to this. Common for `.heic` on Chrome and Edge on Windows without the
 * HEIF extension, and for `.psd` on several browsers -- both offered by the picker and listed as
 * allowed in the help text, so refusing them blocked a passenger over their own browser's
 * ignorance, on a file that was accepted before this change.
 *
 * Tolerated by the validator, NOT offered in the input's `accept`: putting it there would make the
 * picker show every file and undo the filter.
 *
 * Letting it through costs nothing, because the client is not the enforcement boundary.
 * `PassengerDocumentUploader` in platform-purchase derives the content type from the bytes, so a
 * renamed `.exe` is refused there whatever the browser called it.
 *
 * @type {string}
 */
export const UNRECOGNISED_DOCUMENT_TYPE = 'application/octet-stream';
